import { useEffect, useState } from "react";
import { NavigateBeforeIcon, NavigateNextIcon } from "../../../icons";
import { formatByTypeDate } from "../../../utils/utils";
import { Box, Button, Grid } from "@mui/material";

interface DateRangePickerProps {
  dateRange: {
    start_date: string;
    end_date: string;
  };
  onApply: (range: { start_date: string; end_date: string }) => void;
  onCancel: () => void;
}

const DatePickerTypeButtonStyles = {
  padding: "0.25rem 0.75rem",
  fontSize: "0.75rem",
  borderRadius: "9999px",
  backgroundColor: "white",
  border: "1px solid #d1d5db",
  cursor: "pointer",
  transition: "background-color 0.2s",
};

export default function DateRangePicker({
  dateRange,
  onApply,
  onCancel,
}: DateRangePickerProps) {
  const parseDate = (dateStr: string) => {
    return dateStr ? new Date(dateStr) : null;
  };

  const [startDate, setStartDate] = useState<Date | null>(
    parseDate(dateRange.start_date)
  );
  const [endDate, setEndDate] = useState<Date | null>(
    parseDate(dateRange.end_date)
  );
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tempStart, setTempStart] = useState<Date | null>(
    parseDate(dateRange.start_date)
  );
  const [tempEnd, setTempEnd] = useState<Date | null>(
    parseDate(dateRange.end_date)
  );
  const [selectingDate, setSelctingDate] = useState<boolean>(false);

  useEffect(() => {
    console.log(dateRange);
  }, []);

  const getMonthName = (date: Date): string => {
    return date.toLocaleString("ko-KR", { month: "long", year: "numeric" });
  };

  const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number): number => {
    return new Date(year, month, 1).getDay();
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const handleDateClick = (date: Date) => {
    if (!selectingDate) {
      setTempStart(date);
      setTempEnd(date);
      setSelctingDate(true);
    } else {
      const [start, end] =
        date < tempStart! ? [date, tempStart] : [tempStart, date];
      setStartDate(start);
      setEndDate(end);
      setTempStart(start);
      setTempEnd(end);
      setSelctingDate(false);
    }
  };

  const handlePrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  const handleApply = () => {
    const hasDateRange = startDate && endDate;

    onApply({
      start_date: hasDateRange ? formatByTypeDate(startDate) : "",
      end_date: hasDateRange ? formatByTypeDate(endDate) : "",
    });

    onCancel();
  };

  const handlePresetRange = (days: number) => {
    if (days === 0) {
      setStartDate(null);
      setEndDate(null);
      setCurrentMonth(new Date());
    } else {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - days + 1);
      setStartDate(start);
      setEndDate(end);
      setCurrentMonth(start);
    }
  };

  const isInRange = (date: Date) => {
    // selectingDate 중일 땐 tempStart~tempEnd로 계산
    if (selectingDate && tempStart && tempEnd) {
      const [start, end] =
        tempStart < tempEnd ? [tempStart, tempEnd] : [tempEnd, tempStart];
      return date >= start && date <= end;
    }

    // 선택 완료 후는 startDate~endDate로 계산
    if (startDate && endDate) {
      return date >= startDate && date <= endDate;
    }

    return false;
  };

  const isSameDay = (d1: Date, d2: Date) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  const isSelected = (date: Date) => {
    if (!selectingDate) {
      return (
        (startDate && isSameDay(date, startDate)) ||
        (endDate && isSameDay(date, endDate))
      );
    } else {
      return (
        (tempStart && isSameDay(date, tempStart)) ||
        (tempEnd && isSameDay(date, tempEnd))
      );
    }
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month);

    const days = [];

    // 빈칸 채우기 (월 시작 전 요일 공백)
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<Box key={`empty-${i}`} sx={{ width: 40, height: 40 }} />);
    }

    // 실제 날짜 렌더링
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateIsInRange = isInRange(date);
      const dateIsSelected = isSelected(date);
      const dateIsToday = isToday(date);

      days.push(
        <Button
          key={`day-${day}`}
          onClick={() => handleDateClick(date)}
          variant={dateIsSelected ? "contained" : "text"}
          sx={{
            width: 40,
            height: 40,
            minWidth: 0,
            padding: 0,
            borderRadius: "50%",
            backgroundColor: dateIsSelected
              ? "#3B82F6"
              : dateIsInRange
              ? "#E3EFFF"
              : "transparent",
            color: dateIsSelected
              ? "#fff"
              : dateIsToday
              ? "#3B82F6"
              : "text.primary",
            border: dateIsToday && !dateIsSelected ? "1px solid" : "none",
            borderColor: "#3B82F6",
            "&:hover": {
              backgroundColor: dateIsSelected
                ? "primary.dark"
                : dateIsInRange
                ? "primary.light"
                : "grey.100",
            },
          }}
        >
          {day}
        </Button>
      );
    }
    return (
      <Grid container spacing={1} columns={7}>
        {days.map((dayEl, idx) => (
          <Grid item xs={1} key={idx}>
            {dayEl}
          </Grid>
        ))}
      </Grid>
    );
  };

  const presets = [
    { label: "최근 7일", days: 7 },
    { label: "최근 30일", days: 30 },
    { label: "최근 90일", days: 90 },
    { label: "최근 1년", days: 365 },
    { label: "전체", days: 0 },
  ];

  return (
    <div
      style={{
        position: "absolute",
        backgroundColor: "white",
        borderRadius: "0.5rem",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        border: "1px solid #e5e7eb",
        overflow: "hidden",
        width: "400px",
        marginTop: "3px",
        zIndex: 999,
      }}
    >
      <div
        style={{
          padding: "0.75rem",
          borderBottom: "1px solid #e5e7eb",
          backgroundColor: "#f9fafb",
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
          {presets.map((preset) => (
            <button
              key={preset.label}
              onClick={() => handlePresetRange(preset.days)}
              style={DatePickerTypeButtonStyles}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f3f4f6")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "white")
              }
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: "1rem" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "1rem",
          }}
        >
          <button
            onClick={handlePrevMonth}
            style={{
              padding: "0.25rem",
              borderRadius: "9999px",
              cursor: "pointer",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#f3f4f6")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
          >
            <NavigateBeforeIcon />
          </button>
          <div style={{ fontSize: "0.875rem", fontWeight: 500 }}>
            {getMonthName(currentMonth)}
          </div>
          <button
            onClick={handleNextMonth}
            style={{
              padding: "0.25rem",
              borderRadius: "9999px",
              cursor: "pointer",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#f3f4f6")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
          >
            <NavigateNextIcon />
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: "0.25rem",
            marginBottom: "0.5rem",
          }}
        >
          {["일", "월", "화", "수", "목", "금", "토"].map((day) => (
            <div
              key={day}
              style={{
                height: "2rem",
                width: "2rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.75rem",
                color: "#6b7280",
              }}
            >
              {day}
            </div>
          ))}
        </div>

        {/* <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: "0.25rem",
          }}
        > */}
        {renderCalendar()}
        {/* </div> */}
      </div>
      <div
        style={{
          padding: "0.75rem 1rem",
          backgroundColor: "#f9fafb",
          borderTop: "1px solid #e5e7eb",
        }}
      >
        <div style={{ fontSize: "0.75rem", marginBottom: "0.5rem" }}>
          <span style={{ fontWeight: 500 }}>선택된 기간:</span>{" "}
          {startDate && endDate
            ? `${formatByTypeDate(startDate)} ~ ${formatByTypeDate(endDate)}`
            : "전체 기간"}
        </div>
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}
        >
          <Button
            sx={{
              padding: "0.25rem 0.75rem",
              fontSize: "0.875rem",
              backgroundColor: "#b7b7b7",
              color: "white",
              borderRadius: "0.375rem",
              border: "none",
              cursor: "pointer",
              transition: "background-color 0.2s",
              "&:hover": {
                backgroundColor: "gray",
              },
            }}
            onClick={onCancel}
          >
            취소
          </Button>
          <Button
            sx={{
              padding: "0.25rem 0.75rem",
              fontSize: "0.875rem",
              backgroundColor: "#3b82f6",
              color: "white",
              borderRadius: "0.375rem",
              border: "none",
              cursor: "pointer",
              transition: "background-color 0.2s",
              "&:hover": {
                backgroundColor: "#2563eb",
              },
            }}
            onClick={handleApply}
          >
            적용
          </Button>
        </Box>
      </div>
    </div>
  );
}
